import { UploadIcon } from "@/assets/icons";
import { cn } from "@/lib/utils";
import { Button, Flex, Text } from "@radix-ui/themes";
import type { ReactNode } from "react";
import type { DropzoneState } from "react-dropzone";

export type UploadDropAreaProps = {
  dropzone: Pick<
    DropzoneState,
    "getRootProps" | "getInputProps" | "isDragActive"
  >;
  title: string;
  dropTitle: string;
  requirements: ReactNode;
  // Replaces the empty state (e.g. the upload queue).
  children?: ReactNode;
  interactive?: boolean;
  className?: string;
  testId?: string;
  inputTestId?: string;
  browseTestId?: string;
};

// Shared drop area for the audio, mastering, artwork and profile photo modals.
export default function UploadDropArea({
  dropzone: { getRootProps, getInputProps, isDragActive },
  title,
  dropTitle,
  requirements,
  children,
  interactive = true,
  className,
  testId,
  inputTestId,
  browseTestId,
}: UploadDropAreaProps) {
  return (
    <div
      {...getRootProps()}
      data-testid={testId}
      className={cn(
        "flex flex-col justify-center rounded-2xl border border-dashed transition-colors",
        isDragActive
          ? "border-blue-600 bg-blue-100"
          : "border-blue-200 bg-blue-100/40",
        children ? "p-5" : "p-8",
        interactive && "cursor-pointer",
        className,
      )}
    >
      <input {...getInputProps()} data-testid={inputTestId} />

      {children ? (
        children
      ) : (
        <Flex
          direction="column"
          align="center"
          justify="center"
          gap="4"
          className="py-6 text-center"
        >
          <Flex
            align="center"
            justify="center"
            className="h-16 w-16 rounded-full bg-blue-200/50 text-blue-600"
          >
            <UploadIcon className="h-7 w-7 fill-current" />
          </Flex>
          {/* Phones can't drag files: the button is enough. */}
          <Text className="hidden font-figtree text-xl font-bold text-blue-900 md:block">
            {isDragActive ? dropTitle : title}
          </Text>
          {!isDragActive && (
            <Button
              type="button"
              size="3"
              radius="full"
              className="bg-blue-600"
              data-testid={browseTestId}
            >
              Browse files
            </Button>
          )}
          <Text className="font-open-sans text-sm text-text-secondary">
            {requirements}
          </Text>
        </Flex>
      )}
    </div>
  );
}
